import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Lock, 
  Unlock, 
  Trash2, 
  Eye, 
  X, 
  Search, 
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  CreditCard,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Shield,
  Ban,
  MessageCircle,
  Plus
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { 
  searchUsers,
  updateUserProfile,
  getUserProfile
} from '../../firebase/collections';
import toast from 'react-hot-toast';

const CoachingManagement = () => {
  const navigate = useNavigate();
  const { logAdminAction } = useRealtime();
  const [coachings, setCoachings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSubscription, setFilterSubscription] = useState('');
  const [selectedCoaching, setSelectedCoaching] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load all coachings
  useEffect(() => {
    loadCoachings();
  }, []);

  const loadCoachings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Query all users with role 'coaching_admin'
      const result = await searchUsers('', { role: 'coaching_admin' });
      
      if (result.success) {
        setCoachings(result.data || []);
      } else {
        setError('Failed to load coachings: ' + result.error);
        toast.error('Failed to load coachings');
      }
    } catch (error) {
      setError('Error loading coachings: ' + error.message);
      toast.error('Error loading coachings');
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionStatus = (coaching) => {
    if (!coaching.subscription) return { status: 'inactive', label: 'No Subscription', color: 'bg-gray-100 text-gray-800' };
    
    const subStatus = coaching.subscription.status || 'inactive';
    
    if (subStatus === 'active') {
      // Check if subscription has expired
      if (coaching.subscription.endDate) {
        const endDate = coaching.subscription.endDate?.toDate 
          ? coaching.subscription.endDate.toDate() 
          : new Date(coaching.subscription.endDate);
        const isExpired = endDate < new Date();
        if (isExpired) {
          return { status: 'expired', label: 'Expired', color: 'bg-red-100 text-red-800' };
        }
      }
      return { status: 'active', label: 'Active', color: 'bg-green-100 text-green-800' };
    }
    
    if (subStatus === 'pending') {
      return { status: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' };
    }
    
    if (subStatus === 'cancelled') {
      return { status: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' };
    }
    
    return { status: 'inactive', label: 'Inactive', color: 'bg-gray-100 text-gray-800' };
  };

  const getDaysRemaining = (coaching) => {
    if (!coaching.subscription?.endDate) return null;
    const endDate = coaching.subscription.endDate?.toDate 
      ? coaching.subscription.endDate.toDate() 
      : new Date(coaching.subscription.endDate);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date?.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleViewDetails = async (coaching) => {
    try {
      // Fetch latest data
      const result = await getUserProfile(coaching.uid || coaching.id);
      if (result.success) {
        setSelectedCoaching({ ...result.data, id: coaching.uid || coaching.id });
        setShowDetailsModal(true);
      } else {
        setSelectedCoaching(coaching);
        setShowDetailsModal(true);
      }
    } catch (error) {
      setSelectedCoaching(coaching);
      setShowDetailsModal(true);
    }
  };

  const handleLockAccount = async (coaching) => {
    if (!window.confirm(`Are you sure you want to ${coaching.isLocked ? 'unlock' : 'lock'} this coaching account?`)) {
      return;
    }

    setActionLoading(coaching.uid || coaching.id);
    try {
      const updateData = {
        isLocked: !coaching.isLocked,
        lockedAt: !coaching.isLocked ? new Date() : null,
        updatedAt: new Date()
      };

      const result = await updateUserProfile(coaching.uid || coaching.id, updateData);
      
      if (result.success) {
        toast.success(`Account ${coaching.isLocked ? 'unlocked' : 'locked'} successfully`);
        await logAdminAction('coaching_locked', { 
          coachingId: coaching.coachingId, 
          action: coaching.isLocked ? 'unlock' : 'lock' 
        });
        await loadCoachings();
      } else {
        toast.error('Failed to update account status');
      }
    } catch (error) {
      toast.error('Error updating account: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleTerminateAccount = async (coaching) => {
    const confirmText = `TERMINATE`;
    const userInput = window.prompt(
      `WARNING: This will permanently terminate the coaching account.\n\n` +
      `This action cannot be undone. Type "${confirmText}" to confirm:`
    );

    if (userInput !== confirmText) {
      return;
    }

    setActionLoading(coaching.uid || coaching.id);
    try {
      const updateData = {
        isActive: false,
        isTerminated: true,
        terminatedAt: new Date(),
        updatedAt: new Date()
      };

      const result = await updateUserProfile(coaching.uid || coaching.id, updateData);
      
      if (result.success) {
        toast.success('Account terminated successfully');
        await logAdminAction('coaching_terminated', { 
          coachingId: coaching.coachingId,
          coachingName: coaching.coachingName
        });
        await loadCoachings();
      } else {
        toast.error('Failed to terminate account');
      }
    } catch (error) {
      toast.error('Error terminating account: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleWhatsAppMessage = (coaching) => {
    if (!coaching.phone) {
      toast.error('Phone number not available for this coaching');
      return;
    }

    // Format phone number for WhatsApp (remove spaces, dashes, and ensure it starts with country code)
    let phoneNumber = coaching.phone.replace(/\s+/g, '').replace(/-/g, '').replace(/\+/g, '');
    
    // If phone doesn't start with country code, assume it's Indian (+91)
    if (!phoneNumber.startsWith('91') && phoneNumber.length === 10) {
      phoneNumber = '91' + phoneNumber;
    }

    // Create the message
    const coachingName = coaching.coachingName || 'your coaching';
    const message = `Hello ${coaching.contactPerson || 'there'},

This is a reminder from ${process.env.REACT_APP_SITE_NAME || 'our platform'} regarding your coaching account "${coachingName}".

⚠️ IMPORTANT: Your subscription is currently inactive. 

Please renew your subscription within 7 days, or your account will be automatically locked.

To renew your subscription, please visit your coaching dashboard or contact our support team.

Thank you for your understanding.

Best regards,
Admin Team`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp in a new window/tab
    window.open(whatsappUrl, '_blank');
    
    // Log the action
    logAdminAction('whatsapp_sent', {
      coachingId: coaching.coachingId,
      coachingName: coaching.coachingName,
      phone: coaching.phone
    });
    
    toast.success('Opening WhatsApp...');
  };

  const filteredCoachings = coachings.filter(coaching => {
    const matchesSearch = 
      coaching.coachingName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coaching.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coaching.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coaching.coachingId?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !filterStatus || 
      (filterStatus === 'active' && coaching.isActive && !coaching.isTerminated) ||
      (filterStatus === 'locked' && coaching.isLocked) ||
      (filterStatus === 'terminated' && coaching.isTerminated) ||
      (filterStatus === 'inactive' && !coaching.isActive && !coaching.isTerminated);
    
    const matchesSubscription = !filterSubscription || 
      getSubscriptionStatus(coaching).status === filterSubscription;
    
    return matchesSearch && matchesStatus && matchesSubscription;
  });

  if (loading) {
    return (
      <div className="admin-content flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading coachings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="admin-section-header">
        <div>
          <h1 className="admin-section-title">
            <Building2 className="inline mr-3 text-blue-600" size={28} />
            Coaching Management
          </h1>
          <p className="admin-section-subtitle">
            Manage all registered coaching accounts, subscriptions, and access
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => navigate('/admin/create-institute-account')}
            className="admin-btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Offline Account
          </button>
          <button 
            onClick={loadCoachings}
            className="admin-btn-secondary"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="admin-card p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>{error}</span>
            </div>
            <button 
              onClick={() => setError(null)}
              className="text-red-700 hover:text-red-900"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="admin-card p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 mr-2" />
              <span>{success}</span>
            </div>
            <button 
              onClick={() => setSuccess(null)}
              className="text-green-700 hover:text-green-900"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="admin-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search coachings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Account Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="locked">Locked</option>
              <option value="inactive">Inactive</option>
              <option value="terminated">Terminated</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Subscription
            </label>
            <select
              value={filterSubscription}
              onChange={(e) => setFilterSubscription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">All Subscriptions</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
                setFilterSubscription('');
              }}
              className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="admin-grid-stats">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-stat-card blue"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Coachings
              </p>
              <p className="text-2xl font-bold admin-gradient-text">
                {coachings.length}
              </p>
            </div>
            <Building2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="admin-stat-card green"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Active Subscriptions
              </p>
              <p className="text-2xl font-bold admin-gradient-text-success">
                {coachings.filter(c => getSubscriptionStatus(c).status === 'active').length}
              </p>
            </div>
            <CreditCard className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="admin-stat-card yellow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Locked Accounts
              </p>
              <p className="text-2xl font-bold admin-gradient-text-warning">
                {coachings.filter(c => c.isLocked).length}
              </p>
            </div>
            <Lock className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="admin-stat-card red"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Terminated
              </p>
              <p className="text-2xl font-bold admin-gradient-text-danger">
                {coachings.filter(c => c.isTerminated).length}
              </p>
            </div>
            <Ban className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
        </motion.div>
      </div>

      {/* Coachings Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Coaching Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Contact Person
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Subscription
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Account Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredCoachings.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                      {coachings.length === 0 
                        ? 'No coachings registered yet' 
                        : 'No coachings match your filters'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredCoachings.map((coaching) => {
                  const subStatus = getSubscriptionStatus(coaching);
                  const daysRemaining = getDaysRemaining(coaching);
                  const isLoading = actionLoading === (coaching.uid || coaching.id);

                  return (
                    <tr key={coaching.uid || coaching.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building2 className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {coaching.coachingName || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              ID: {coaching.coachingId || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {coaching.contactPerson || 'N/A'}
                        </div>
                        {coaching.phone && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            <Phone className="inline h-3 w-3 mr-1" />
                            {coaching.phone}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {coaching.email || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${subStatus.color}`}>
                            <CreditCard className="h-3 w-3 mr-1" />
                            {subStatus.label}
                          </span>
                          {subStatus.status === 'active' && daysRemaining !== null && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {daysRemaining} days remaining
                            </span>
                          )}
                          {coaching.subscription?.plan && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Plan: {coaching.subscription.plan}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          {coaching.isLocked ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                              <Lock className="h-3 w-3 mr-1" />
                              Locked
                            </span>
                          ) : coaching.isTerminated ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                              <Ban className="h-3 w-3 mr-1" />
                              Terminated
                            </span>
                          ) : coaching.isActive ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                              <XCircle className="h-3 w-3 mr-1" />
                              Inactive
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewDetails(coaching)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          {/* WhatsApp button - only show if subscription is not active */}
                          {subStatus.status !== 'active' && coaching.phone && !coaching.isTerminated && (
                            <button
                              onClick={() => handleWhatsAppMessage(coaching)}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                              title="Send WhatsApp Reminder"
                            >
                              <MessageCircle size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleLockAccount(coaching)}
                            disabled={isLoading || coaching.isTerminated}
                            className={`${
                              coaching.isLocked 
                                ? 'text-green-600 hover:text-green-900 dark:text-green-400' 
                                : 'text-yellow-600 hover:text-yellow-900 dark:text-yellow-400'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                            title={coaching.isLocked ? 'Unlock Account' : 'Lock Account'}
                          >
                            {coaching.isLocked ? <Unlock size={18} /> : <Lock size={18} />}
                          </button>
                          <button
                            onClick={() => handleTerminateAccount(coaching)}
                            disabled={isLoading || coaching.isTerminated}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Terminate Account"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedCoaching && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="admin-card max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Coaching Details
              </h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedCoaching(null);
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Building2 className="mr-2" size={20} />
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Coaching Name
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedCoaching.coachingName || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Coaching ID
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedCoaching.coachingId || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Contact Person
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedCoaching.contactPerson || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white flex items-center">
                      <Mail className="h-4 w-4 mr-1" />
                      {selectedCoaching.email || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white flex items-center">
                      <Phone className="h-4 w-4 mr-1" />
                      {selectedCoaching.phone || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      User ID
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white font-mono text-xs">
                      {selectedCoaching.uid || selectedCoaching.id || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              {selectedCoaching.address && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <MapPin className="mr-2" size={20} />
                    Address
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Street Address
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {selectedCoaching.address.street || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        City
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {selectedCoaching.address.city || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        State
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {selectedCoaching.address.state || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Pincode
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {selectedCoaching.address.pincode || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Subscription Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <CreditCard className="mr-2" size={20} />
                  Subscription Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSubscriptionStatus(selectedCoaching).color}`}>
                      {getSubscriptionStatus(selectedCoaching).label}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Plan
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedCoaching.subscription?.plan || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start Date
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(selectedCoaching.subscription?.startDate)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      End Date
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(selectedCoaching.subscription?.endDate)}
                    </p>
                  </div>
                  {getDaysRemaining(selectedCoaching) !== null && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Days Remaining
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {getDaysRemaining(selectedCoaching)} days
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Account Status */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Shield className="mr-2" size={20} />
                  Account Status
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Active
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedCoaching.isActive ? (
                        <span className="text-green-600">Yes</span>
                      ) : (
                        <span className="text-red-600">No</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Locked
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedCoaching.isLocked ? (
                        <span className="text-red-600">Yes</span>
                      ) : (
                        <span className="text-green-600">No</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Terminated
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedCoaching.isTerminated ? (
                        <span className="text-red-600">Yes</span>
                      ) : (
                        <span className="text-green-600">No</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Created At
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(selectedCoaching.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedCoaching(null);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CoachingManagement;

